import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { ROUTER_PATHS } from '@/shared';
import { useFetchParametros } from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  ModalObservacionComponente,
  SingleTableBoxScene,
} from '@/shared/components/ui';
import { TABLE_CONSTANTS } from '@/shared/constants/ui';
import { useTableServerSideFiltering } from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { Parametro } from '@/shared/interfaces';
import {
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasPermission,
} from '@/shared/utils';
import { useUiConfirmModalStore } from '@/store/ui';



export type ParametrosPageProps = {};

const ParametrosPage: React.FC<ParametrosPageProps> = () => {
  const navigate = useNavigate();
  const returnUrlParametrosPage =
    ROUTER_PATHS.administration.systemParametersNav;
  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: ParametrosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchParametros({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      nombre: searchTerm,
      ...filterObject,
      filterByState: false,
    },
  });

  ///* handlers
  const onEdit = (parametro: Parametro) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar Parámetro',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlParametrosPage}/editar/${parametro.id_parametro}`);
      },
    });
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Parametro>[]>(
    () => [
      {
        accessorKey: 'slug',
        header: 'SLUG',
        size: 333,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => (
          <ModalObservacionComponente
            observacion_cliente={row.original?.slug || ''}
          />
        ),
      },

      {
        accessorKey: 'nombre',
        header: 'NOMBRE',
        size: 222,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => (
          <ModalObservacionComponente
            observacion_cliente={row.original?.nombre || ''}
          />
        ),
      },

      {
        accessorKey: 'valor',
        header: 'VALOR',
        size: 210,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'valor'),
      },

      {
        accessorKey: 'tipo',
        header: 'TIPO',
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo'),
      },

      {
        accessorKey: 'descripcion',
        header: 'DESCRIPCION',
        size: 280,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => (
          <ModalObservacionComponente
            observacion_cliente={row.original?.descripcion || ''}
          />
        ),
      },

      {
        accessorKey: 'codigo',
        header: 'CODIGO',
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => (
          <ModalObservacionComponente
            observacion_cliente={row.original?.codigo || ''}
          />
        ),
      },

      {
        accessorKey: 'fecha_creacion',
        header: 'CREADO',
        size: 154,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: 'fecha_modificacion',
        header: 'MODIFICADO',
        size: 154,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [setConfirmDialog, setConfirmDialogIsOpen]
  );

  return (
    <SingleTableBoxScene
      title="Parámetros del Sistema"
      urlRedirect={`${returnUrlParametrosPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_parametro)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<Parametro>
        columns={columns}
        data={ParametrosPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ParametrosPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_parametro
        )}
        enableEditing={hasPermission(PermissionsEnum.api_change_parametro)}
        actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasPermission(PermissionsEnum.api_change_parametro)}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default ParametrosPage;
